import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Globe, X, RefreshCcw } from 'lucide-react';
import  Card  from '@/components/ui/Card';
import  Button  from '@/components/ui/Button';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { adminApi } from '../../../services/apiClient';

const SessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await adminApi.getCurrentSession();
      setSessions(response.data.sessions);
    } catch (err) {
      setError('Failed to fetch active sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      await adminApi.terminateSession(sessionId);
      setSuccess('Session terminated successfully');
      fetchSessions();
    } catch (err) {
      setError('Failed to terminate session');
    }
  };

  const handleTerminateOtherSessions = async () => {
    try {
      await adminApi.terminateOtherSessions();
      setSuccess('All other sessions terminated successfully');
      fetchSessions();
    } catch (err) {
      setError('Failed to terminate other sessions');
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'desktop':
        return <Monitor className="w-5 h-5" />;
      case 'mobile':
        return <Smartphone className="w-5 h-5" />;
      default:
        return <Globe className="w-5 h-5" />;
    }
  };

  if (loading) {
    return <div>Loading sessions...</div>;
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Active Sessions</h2>
          <p className="text-gray-600">Manage your active sessions across devices</p>
        </div>
        <Button
          variant="outline"
          onClick={handleTerminateOtherSessions}
          className="flex items-center"
        >
          <X className="w-4 h-4 mr-2" />
          Terminate Other Sessions
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {sessions.map((session) => (
          <div
            key={session.id}
            className="flex items-center justify-between p-4 border rounded-lg"
          >
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                {getDeviceIcon(session.deviceType)}
              </div>
              <div>
                <p className="font-medium">{session.deviceName}</p>
                <p className="text-sm text-gray-600">
                  {session.location} • Last active: {new Date(session.lastActive).toLocaleString()}
                </p>
              </div>
              {session.current && (
                <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                  Current Session
                </span>
              )}
            </div>
            {!session.current && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTerminateSession(session.id)}
                className="text-red-600 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center text-sm text-gray-600">
        <RefreshCcw className="w-4 h-4 mr-2" />
        <span>Sessions are automatically refreshed every 5 minutes</span>
      </div>
    </Card>
  );
};

export default SessionManagement;